<template lang="pug">
v-card.pa-4.mt-4
  h1 Verantwoord Krediet Maximum berekenen

  v-divider.mb-4.mt-4

  v-layout(column)
    label: b Burgelijke staat*
    v-select(
      placeholder='Maak een keuze',
      v-model='maritalStatus',
      :items='maritalStatusItems',
      :error-messages='maritalStatusValidationErrors',
      @change='$v.maritalStatus.$touch(); delayCalculate()',
      @blur='$v.maritalStatus.$touch()',
      solo
      )

  v-layout(column)
    label: b Woonsituatie*
    v-select(
      placeholder='Maak een keuze',
      v-model='livingSituation',
      :items='livingSituationItems',
      :error-messages='livingSituationValidationErrors',
      @change='$v.livingSituation.$touch(); delayCalculate()',
      @blur='$v.livingSituation.$touch()',
      solo
      )

  euro-field.mr-0(
    v-model.number='netIncome',
    label='Netto maandinkomen',
    required,
    :error-messages='netIncomeValidationErrors',
    @change='$v.netIncome.$touch()',
    @blur='$v.netIncome.$touch()',
    @keyup='delayCalculate()'
    )

  euro-field.mr-0(
    v-if='hasPartner',
    v-model.number='partnerNetIncome',
    label='Netto maandinkomen partner',
    required,
    :error-messages='partnerNetIncomeValidationErrors',
    @change='$v.partnerNetIncome.$touch()',
    @blur='$v.partnerNetIncome.$touch()',
    @keyup='delayCalculate()'
    )

  euro-field.mr-0(
    v-if='livingSituation',
    v-model.number='housingChargesMonthly',
    :label='housingChargesMonthlyLabel',
    required,
    :error-messages='housingChargesMonthlyValidationErrors',
    @change='$v.housingChargesMonthly.$touch()',
    @blur='$v.housingChargesMonthly.$touch()',
    @keyup='delayCalculate()'
    )

  transition(name='slidedown')
    v-card.pa-4.result(v-if='vkmResult !== null || loading')
      v-layout(align-center)
        v-progress-linear(indeterminate, v-if='loading')
        v-layout.pmt.input-row.flex-column-phone.flex-justify-center-phone(align-center, v-else)
          label.pr-4
            h1 Je mag lenen:
          transition(name='fade', mode='out-in')
            h1.primary--text {{ vkmResult | currency }}
</template>

<script>
import required from 'vuelidate/src/validators/required'
import requiredIf from 'vuelidate/src/validators/requiredIf'
import decimal from 'vuelidate/src/validators/decimal'
import axios from 'axios'

export default {
  data () {
    return {
      maritalStatus: null,
      livingSituation: null,
      netIncome: null,
      partnerNetIncome: null,
      housingChargesMonthly: null,

      vkmResult: null,

      calculateTimer: null,
      loading: false,

      maritalStatusItems: [
        { text: 'Gehuwd / samenwonend met kinderen', value: 'married_or_living_together_with_children' },
        { text: 'Gehuwd / samenwonend', value: 'married_or_living_together' },
        { text: 'Alleenstaand met kinderen', value: 'single_with_children' },
        { text: 'Alleenstaand', value: 'single' }
      ],

      livingSituationItems: [
        { text: 'Koophuis', value: 'owner' },
        { text: 'Huurhuis', value: 'renter' },
        { text: 'Inwonend', value: 'habitant' }
      ]
    }
  },

  computed: {
    housingChargesMonthlyLabel () {
      if (this.livingSituation === 'owner') return 'Bruto hypotheeklast per maand'
      else if (this.livingSituation === 'renter') return 'Maandhuur'
      else if (this.livingSituation === 'habitant') return 'Kostgeld'
    },

    hasPartner () {
      return this.maritalStatus === 'married_or_living_together_with_children' || this.maritalStatus === 'married_or_living_together'
    },

    maritalStatusValidationErrors () {
      const errors = []
      if (!this.$v.maritalStatus.$dirty) return errors
      !this.$v.maritalStatus.required && errors.push('Dit veld is verplicht')
      return errors
    },

    livingSituationValidationErrors () {
      const errors = []
      if (!this.$v.maritalStatus.$dirty) return errors
      !this.$v.maritalStatus.required && errors.push('Dit veld is verplicht')
      return errors
    },

    netIncomeValidationErrors () {
      const errors = []
      if (!this.$v.netIncome.$dirty) return errors
      !this.$v.netIncome.decimal && errors.push('Dit moet een getal zijn')
      !this.$v.netIncome.required && errors.push('Dit veld is verplicht')
      return errors
    },

    partnerNetIncomeValidationErrors () {
      const errors = []
      if (!this.$v.partnerNetIncome.$dirty) return errors
      !this.$v.partnerNetIncome.decimal && errors.push('Dit moet een getal zijn')
      !this.$v.partnerNetIncome.requiredIf && errors.push('Dit veld is verplicht')
      return errors
    },

    housingChargesMonthlyValidationErrors () {
      const errors = []
      if (!this.$v.housingChargesMonthly.$dirty) return errors
      !this.$v.housingChargesMonthly.decimal && errors.push('Dit moet een getal zijn')
      !this.$v.housingChargesMonthly.required && errors.push('Dit veld is verplicht')
      return errors
    }
  },

  methods: {
    delayCalculate () {
      clearTimeout(this.calculateTimer)
      this.loading = false
      this.vkmResult = null

      if (this.$v.$invalid) return

      this.loading = true
      this.calculateTimer = setTimeout(() => {
        this.calculate()
      }, 2000)
    },

    async calculate () {
      if (this.$v.$invalid) return

      const { data } = await axios({
        method: 'POST',
        url: '/calc/input/vkm',
        params: {
          maritalStatus: this.maritalStatus,
          livingSituation: this.livingSituation,
          netIncome: this.netIncome,
          partnerNetIncome: this.partnerNetIncome,
          housingChargesMonthly: this.housingChargesMonthly
        }
      })

      this.vkmResult = data.result

      this.loading = false
    }
  },

  validations: {
    maritalStatus: {
      required
    },

    livingSituation: {
      required
    },

    netIncome: {
      required,
      decimal
    },

    partnerNetIncome: {
      requiredIf: (v, vm) => {
        return !vm.hasPartner || v !== null
      },
      decimal
    },

    housingChargesMonthly: {
      required,
      decimal
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.result {
  background: $accent;

  span {
    margin-left: 40px;
    font-size: 18px;
  }
}
</style>
